import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import AutoHeightIframe from './AutoHeightIframe';

// Парсер ONE-PAGER!A1 → массив секций.
// Формат описан в /app/frontend/ONEPAGER_HTML_GUIDE.md
function parseOnePager(text) {
  if (!text || typeof text !== 'string') return [];

  // 1. Делим на секции по `---` на собственной строке
  const sections = text.split(/^\s*---\s*$/m);

  const result = [];
  for (let s of sections) {
    s = s.trim();
    if (!s) continue;

    // 2. Ищем маркеры карточек: === Заголовок === {опции}
    const markerRe = /^===\s*(.*?)\s*===(?:\s*\{([^}]+)\})?\s*$/gm;
    const matches = [...s.matchAll(markerRe)];

    if (matches.length === 0) {
      // Свободная секция (full-width iframe)
      result.push({ type: 'free', html: s });
      continue;
    }

    // Ряд карточек
    const cards = [];
    for (let i = 0; i < matches.length; i++) {
      const m = matches[i];
      const title = m[1] || '';
      const optsStr = m[2] || '';
      const startIdx = m.index + m[0].length;
      const endIdx = i + 1 < matches.length ? matches[i + 1].index : s.length;
      const html = s.slice(startIdx, endIdx).trim();

      // Парсим опции key:value, key:value
      const opts = {};
      if (optsStr) {
        optsStr.split(',').forEach((part) => {
          const [k, v] = part.split(':').map((x) => (x || '').trim());
          if (!k || v === undefined) return;
          const n = parseInt(v, 10);
          opts[k] = Number.isNaN(n) ? v : n;
        });
      }
      cards.push({ title, html, opts });
    }
    result.push({ type: 'row', cards });
  }
  return result;
}

const Card = ({ title, html, opts }) => {
  const fixedHeight = opts && typeof opts.height === 'number' ? opts.height : undefined;
  const span = opts && typeof opts.span === 'number' ? opts.span : 1;

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
      style={{ gridColumn: `span ${span} / span ${span}` }}
      data-testid="onepager-card"
    >
      {title && (
        <h2
          className="font-benzin text-white text-[clamp(16px,1.4vw,22px)] px-[clamp(20px,2.5vw,32px)] pt-[clamp(20px,2.2vw,28px)] pb-[clamp(8px,1vw,12px)] border-b border-white/5"
          data-testid="onepager-card-title"
        >
          {title}
        </h2>
      )}
      <div className="flex-1">
        <AutoHeightIframe html={html} fixedHeight={fixedHeight} />
      </div>
    </div>
  );
};

const Row = ({ cards }) => {
  // Колонок в grid = сумма span'ов всех карточек (минимум число карточек)
  const totalSpan = cards.reduce(
    (acc, c) => acc + (c.opts && typeof c.opts.span === 'number' ? c.opts.span : 1),
    0
  );
  const cols = Math.max(totalSpan, cards.length);

  return (
    <div
      className="grid gap-[clamp(16px,1.5vw,24px)]"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {cards.map((c, i) => (
        <Card key={i} title={c.title} html={c.html} opts={c.opts} />
      ))}
    </div>
  );
};

const Free = ({ html }) => (
  <div className="w-full" data-testid="onepager-free">
    <AutoHeightIframe html={html} />
  </div>
);

/**
 * OnePager — рендер кабинета инвестора из ONE-PAGER!A1.
 * props:
 *   content — строка из ячейки A1
 */
const OnePager = ({ content }) => {
  const sections = useMemo(() => parseOnePager(content), [content]);

  if (!sections.length) {
    return (
      <div className="text-center text-white/40 text-sm py-12" data-testid="onepager-empty">
        Информация скоро появится.
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-[clamp(20px,2.5vw,40px)]"
      data-testid="onepager-root"
    >
      {sections.map((s, i) => (
        <motion.section
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: 0.05 + i * 0.08,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        >
          {s.type === 'free' ? <Free html={s.html} /> : <Row cards={s.cards} />}
        </motion.section>
      ))}
    </div>
  );
};

export default OnePager;
